'use client';

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  mounted: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>('system');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');
  const [mounted, setMounted] = useState(false);

  // Apply theme immediately on mount to minimize flicker
  useLayoutEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    const actualTheme =
      savedTheme && ['light', 'dark', 'system'].includes(savedTheme)
        ? savedTheme
        : 'system';

    const shouldBeDark =
      actualTheme === 'dark' ||
      (actualTheme === 'system' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);
    const actualResolvedTheme = shouldBeDark ? 'dark' : 'light';

    // Apply theme to DOM
    document.documentElement.classList.toggle('dark', shouldBeDark);

    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        shouldBeDark ? '#0a0a0a' : '#ffffff'
      );
    }

    setTheme(actualTheme);
    setResolvedTheme(actualResolvedTheme);
    setMounted(true);
  }, []);

  // Update resolved theme when theme changes or system preference changes
  useEffect(() => {
    if (!mounted) return;

    const updateResolvedTheme = () => {
      const shouldBeDark =
        theme === 'dark' ||
        (theme === 'system' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches);
      const newResolvedTheme = shouldBeDark ? 'dark' : 'light';

      document.documentElement.classList.toggle('dark', shouldBeDark);
      setResolvedTheme(newResolvedTheme);

      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute(
          'content',
          shouldBeDark ? '#0a0a0a' : '#ffffff'
        );
      }
    };

    updateResolvedTheme();

    // Listen for system theme changes when theme is 'system'
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', updateResolvedTheme);
      return () =>
        mediaQuery.removeEventListener('change', updateResolvedTheme);
    }
  }, [theme, mounted]);

  const handleSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, setTheme: handleSetTheme, mounted }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
